import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Timeline from '../components/Timeline';
import StatusBadge from '../components/StatusBadge';
import LocationMap from '../components/LocationMap';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Sparkles, Download, Upload, FileText, File } from 'lucide-react';
import { API, aiApi, casesApi, fileApi } from '../lib/api';
import { toast } from 'sonner';
import SeverityBadge from '../components/SeverityBadge';
import DocumentChecklist from '../components/DocumentChecklist';
import OpponentDataPanel from '../components/OpponentDataPanel';

const DOC_CATEGORIES = [
  { key: 'fuehrerschein', label: 'Führerschein' },
  { key: 'zulassungsschein', label: 'Zulassungsschein' },
  { key: 'versicherung', label: 'Versicherungsunterlagen' },
  { key: 'gutachten', label: 'Gutachten' },
  { key: 'sonstige', label: 'Sonstige' },
];

function readDataUrl(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

export default function CaseDetailPage() {
  const { caseId } = useParams();
  const [caseData, setCaseData] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [docCat, setDocCat] = useState('fuehrerschein');

  const reload = useCallback(async () => {
    try {
      const { data } = await API.get(`/cases/${caseId}`);
      setCaseData(data);
      const { data: fs } = await API.get(`/files/case/${caseId}`);
      setFiles(fs);
    } catch (e) {
      toast.error('Fall konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { reload(); }, [reload]);

  const runAI = async () => {
    setAiLoading(true);
    try {
      const { data } = await API.post(`/ai/report/${caseId}`);
      setCaseData((c) => ({ ...c, ai_analysis: data.analysis, ai_report: data.report, ai_insurance_summary: data.insurance_summary }));
      toast.success('KI-Analyse abgeschlossen');
    } catch (e) {
      toast.error('KI-Analyse fehlgeschlagen');
    } finally {
      setAiLoading(false);
    }
  };

  const downloadPdf = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/ai/pdf/${caseId}`, '_blank');
  };

  const onUploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await readDataUrl(file);
      const b64 = (dataUrl || '').split(',').pop();
      await API.post('/files/upload', {
        case_id: caseId,
        filename: file.name,
        mime_type: file.type || 'application/octet-stream',
        data_base64: b64,
        category: docCat,
      });
      toast.success('Dokument hochgeladen');
      e.target.value = '';
      reload();
    } catch (err) {
      toast.error('Upload fehlgeschlagen');
    }
  };

  if (loading) return (<div className="p-6 text-sm text-slate-600">Lade...</div>);
  if (!caseData) return (<div className="p-6 text-sm text-slate-600">Fall nicht gefunden.</div>);

  const photos = files.filter((f) => f.category === 'photo');
  const documents = files.filter((f) => f.category !== 'photo');

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        <Link to="/portal" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
          <ArrowLeft className="h-4 w-4" /> Zurück zur Übersicht
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-xs text-slate-500 font-mono">{caseData.case_number}</div>
            <h1 className="font-heading font-semibold text-2xl sm:text-3xl text-[hsl(var(--primary))]">{caseData.vehicle.make} {caseData.vehicle.model}</h1>
            <div className="text-sm text-slate-600">Kennzeichen: {caseData.vehicle.license_plate}</div>
          </div>
          <StatusBadge status={caseData.status} />
        </div>

        <Tabs defaultValue="timeline" className="w-full">
          <TabsList className="grid grid-cols-5 sm:w-fit">
            <TabsTrigger value="timeline" data-testid="tab-timeline">Status</TabsTrigger>
            <TabsTrigger value="ai" data-testid="tab-ai">KI-Bericht</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents">Dokumente</TabsTrigger>
            <TabsTrigger value="opponent" data-testid="tab-opponent">Gegner</TabsTrigger>
            <TabsTrigger value="info" data-testid="tab-info">Info</TabsTrigger>
          </TabsList>

          <TabsContent value="timeline" className="mt-4 space-y-4">
            <DocumentChecklist caseId={caseId} testid="case-doc-checklist" />
            <Card className="rounded-2xl border-slate-200">
              <CardContent className="p-5">
                <Timeline steps={caseData.timeline} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="ai" className="mt-4 space-y-4">
            <Card className="rounded-2xl border-slate-200">
              <CardContent className="p-5">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-[hsl(var(--accent))]" />
                    <div className="font-heading font-semibold text-slate-900">KI Schadensanalyse</div>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={runAI} disabled={aiLoading || photos.length === 0} className="bg-[hsl(var(--primary))] hover:bg-[hsl(210_73%_12%)] text-white" data-testid="case-run-ai-button">
                      {aiLoading ? 'Analysiere...' : 'KI-Analyse starten'}
                    </Button>
                    <Button variant="outline" onClick={downloadPdf} data-testid="case-download-pdf-button">
                      <Download className="h-4 w-4 mr-1.5" /> PDF
                    </Button>
                  </div>
                </div>
                {caseData.ai_analysis ? (
                  <div className="mt-4 text-sm space-y-2">
                    <SeverityBadge ampel={caseData.ai_analysis.ampel} ampelLabel={caseData.ai_analysis.ampel_label} gesamt={caseData.ai_analysis.gesamt_einschaetzung} size="lg" testid="case-severity-badge" />
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
                      <div className="rounded-xl bg-slate-50 p-3">
                        <div className="text-xs text-slate-500">Fahrzeug erkannt</div>
                        <div className="font-medium">{caseData.ai_analysis.fahrzeug_erkannt ? 'Ja' : 'Nein'}</div>
                      </div>
                      <div className="rounded-xl bg-slate-50 p-3">
                        <div className="text-xs text-slate-500">Schäden</div>
                        <div className="font-medium">{(caseData.ai_analysis.schaeden || []).length}</div>
                      </div>
                      <div className="rounded-xl bg-slate-50 p-3">
                        <div className="text-xs text-slate-500">Gesamteinschätzung</div>
                        <div className="font-medium">{caseData.ai_analysis.gesamt_einschaetzung}</div>
                      </div>
                      <div className="rounded-xl bg-slate-50 p-3">
                        <div className="text-xs text-slate-500">Fahrbereit</div>
                        <div className="font-medium">{caseData.ai_analysis.fahrbereit_einschaetzung}</div>
                      </div>
                    </div>
                    <div className="pt-2">
                      <div className="font-medium text-slate-900 mb-1">Erkannte Schäden</div>
                      <ul className="space-y-1">
                        {(caseData.ai_analysis.schaeden || []).map((d, i) => (
                          <li key={i}>• <span className="font-medium">{d.bauteil}</span> — {d.schweregrad} — {d.beschreibung} <span className="text-slate-500">({Math.round((d.konfidenz || 0) * 100)}%)</span></li>
                        ))}
                      </ul>
                    </div>
                    {caseData.ai_analysis.empfehlung && <div className="pt-1"><b>Empfehlung:</b> {caseData.ai_analysis.empfehlung}</div>}
                    <div className="text-[11px] text-slate-500">{caseData.ai_analysis.disclaimer}</div>
                  </div>
                ) : (
                  <div className="mt-3 text-sm text-slate-600">Noch keine KI-Analyse vorhanden. Bitte zuerst Fotos hochladen, dann KI-Analyse starten.</div>
                )}
              </CardContent>
            </Card>
            {caseData.ai_report && (
              <Card className="rounded-2xl border-slate-200">
                <CardContent className="p-5">
                  <div className="font-heading font-semibold text-slate-900 mb-2">Schadensbericht</div>
                  <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans">{caseData.ai_report}</pre>
                </CardContent>
              </Card>
            )}
            {caseData.ai_insurance_summary && (
              <Card className="rounded-2xl border-slate-200">
                <CardContent className="p-5">
                  <div className="font-heading font-semibold text-slate-900 mb-2">Versicherungszusammenfassung</div>
                  <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans">{caseData.ai_insurance_summary}</pre>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="documents" className="mt-4 space-y-4">
            <Card className="rounded-2xl border-slate-200">
              <CardContent className="p-5">
                <div className="font-heading font-semibold text-slate-900 mb-3">Dokumente hochladen</div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {DOC_CATEGORIES.map((c) => (
                    <button key={c.key} type="button" onClick={() => setDocCat(c.key)} className={`px-3 py-2 rounded-xl border text-sm ${docCat === c.key ? 'bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]' : 'bg-white text-slate-800 border-slate-200 hover:bg-slate-50'}`} data-testid={`doc-category-${c.key}`}>{c.label}</button>
                  ))}
                </div>
                <label className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[hsl(var(--accent))] text-white cursor-pointer hover:bg-[hsl(217_90%_48%)]" data-testid="upload-doc-button">
                  <Upload className="h-4 w-4" /> Datei hochladen
                  <input type="file" className="hidden" onChange={onUploadDoc} accept=".pdf,.jpg,.jpeg,.png,.webp" data-testid="upload-doc-input" />
                </label>
              </CardContent>
            </Card>
            <Card className="rounded-2xl border-slate-200">
              <CardContent className="p-5">
                <div className="font-heading font-semibold text-slate-900 mb-3">Hochgeladene Dokumente</div>
                {documents.length === 0 ? (
                  <div className="text-sm text-slate-600">Noch keine Dokumente.</div>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {documents.map((d) => (
                      <li key={d.id} className="py-2 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <File className="h-4 w-4 text-slate-500" />
                          <div className="truncate">
                            <div className="text-sm font-medium truncate">{d.filename}</div>
                            <div className="text-xs text-slate-500">{d.category} • {Math.round((d.size || 0) / 1024)} KB</div>
                          </div>
                        </div>
                        <a href={fileApi.url(d.id)} target="_blank" rel="noreferrer" className="text-sm text-[hsl(var(--primary))] hover:underline">Anzeigen</a>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
            {photos.length > 0 && (
              <Card className="rounded-2xl border-slate-200">
                <CardContent className="p-5">
                  <div className="font-heading font-semibold text-slate-900 mb-3">Unfallfotos</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {photos.map((p) => (
                      <a key={p.id} href={fileApi.url(p.id)} target="_blank" rel="noreferrer" className="block">
                        <img src={fileApi.url(p.id)} alt={p.filename} className="w-full h-32 object-cover rounded-xl border border-slate-200" />
                      </a>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="opponent" className="mt-4">
            <OpponentDataPanel
              value={caseData.opponent || {}}
              onChange={async (opp) => {
                try {
                  await casesApi.updateOpponent(caseId, opp);
                  setCaseData((c) => ({ ...c, opponent: opp }));
                  toast.success('Daten gespeichert');
                } catch (e) {
                  toast.error('Speichern fehlgeschlagen');
                }
              }}
            />
          </TabsContent>

          <TabsContent value="info" className="mt-4 space-y-4">
            <Card className="rounded-2xl border-slate-200">
              <CardContent className="p-5 grid sm:grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500">Kunde:</span> {caseData.person.first_name} {caseData.person.last_name}</div>
                <div><span className="text-slate-500">Telefon:</span> {caseData.person.phone}</div>
                <div><span className="text-slate-500">E-Mail:</span> {caseData.person.email}</div>
                <div><span className="text-slate-500">Datum:</span> {caseData.accident.date} {caseData.accident.time}</div>
                <div className="sm:col-span-2"><span className="text-slate-500">Beschreibung:</span> {caseData.accident.description}</div>
                <div><span className="text-slate-500">Fahrbereit:</span> {caseData.situation.drivable ? 'Ja' : 'Nein'}</div>
                <div><span className="text-slate-500">Polizei:</span> {caseData.situation.police_present ? 'Ja' : 'Nein'}</div>
                <div><span className="text-slate-500">Unfallgegner:</span> {caseData.situation.other_party ? 'Ja' : 'Nein'}</div>
                <div><span className="text-slate-500">Personenschaden:</span> {caseData.situation.personal_injury ? 'Ja' : 'Nein'}</div>
              </CardContent>
            </Card>
            {caseData.location && (
              <Card className="rounded-2xl border-slate-200">
                <CardContent className="p-5">
                  <div className="font-heading font-semibold text-slate-900 mb-3">Unfallort</div>
                  <LocationMap lat={caseData.location.lat} lng={caseData.location.lng} height={260} />
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
